import { useEffect, useState, useRef, useCallback } from 'react';
import { AnimatePresence } from "framer-motion"
import { setReferer, requestToWritePM, setAuthHeadersGetter } from "./util"
import { useNavigationStack, useBackButton, useSwipeBack, useUrlSync, PageRenderer, buildUrlFromPage } from './navigation';
import { TranslationProvider, useTranslation } from './hooks/useTranslation';
import { ToastProvider } from './hooks/useToast';
import Toast from './components/Toast';
import { LoginPage } from './components/LoginPage';

// Platform abstraction imports
import {
  isTelegram,
  isStandalone,
  AuthProvider,
  useAuth,
  ButtonProvider,
  StandaloneButtons,
  ThemeProvider,
  showAlert
} from './platform';
import { StandaloneHeader } from './components/StandaloneHeader';

function AppInner() {
  const { isRTL } = useTranslation();
  const { getAuthHeaders, isLoading: authLoading, isAuthenticated, isGuest } = useAuth();

  // Set up auth headers getter for util.js (synchronous to ensure it's available before child renders)
  setAuthHeadersGetter(getAuthHeaders);

  // Determine initial location based on platform
  let initAppLocation = window.location.hash;

  if (isTelegram) {
    // Telegram WebApp: check start_param for deep linking
    if (typeof window.Telegram?.WebApp?.initDataUnsafe?.start_param === 'string' &&
        window.Telegram.WebApp.initDataUnsafe.start_param.length > 0) {
      initAppLocation = window.Telegram.WebApp.initDataUnsafe.start_param.replace(/__/g, '%')
      try {
        initAppLocation = '/' + decodeURIComponent(initAppLocation)
      } catch (e) {}
    }

    // Extract referer from Telegram context
    let ref = (initAppLocation.match(/\/ref_([0-9_a-zA-Z]+)/) || [])[1];
    if (!ref) {
      const instance = window.Telegram?.WebApp?.initDataUnsafe?.chat_instance;
      if (instance) {
        ref = 'inst' + instance;
      }
    }
    if (ref) {
      setReferer(ref);
    }
  } else {
    // Standalone: use pathname if no hash
    if (!initAppLocation || initAppLocation === '#') {
      const pathname = window.location.pathname;
      if (pathname.startsWith('/app')) {
        const subPath = pathname.replace('/app', '');
        // If we have a specific path, use it; otherwise default to accounts
        if (subPath && subPath !== '/') {
          initAppLocation = subPath;
        } else {
          initAppLocation = '/accounts';
        }
      } else {
        initAppLocation = '/accounts';
      }
    }
  }

  const [appLocation, setAppLocation] = useState(initAppLocation);

  // Create a stable ref for animation directions that persists across renders
  const animationDirectionsRef = useRef("RTL");

  // Use custom hooks for navigation management
  const { navigationStack, chatId, isInitialLoad } = useNavigationStack(appLocation, initAppLocation, animationDirectionsRef);

  // Use custom hook for back button management
  useBackButton(navigationStack, setAppLocation);

  // Sync navigation with browser URL in standalone mode
  useUrlSync(appLocation, setAppLocation, navigationStack);

  // Get current page for swipe logic
  const currentPage = navigationStack[navigationStack.length - 1];

  // Create swipe back handler for non-accounts pages
  const handleSwipeBack = () => {
    if (!currentPage || currentPage.type === 'accounts') {
      return;
    }

    let previousUrl = '/accounts';
    if (navigationStack.length > 1) {
      const previousPage = navigationStack[navigationStack.length - 2];
      previousUrl = buildUrlFromPage(previousPage);
    }
    setAppLocation(previousUrl);
  };

  // Enable swipe back for non-accounts pages
  const isAccountsPage = currentPage && currentPage.type === 'accounts';
  useSwipeBack(handleSwipeBack, !isAccountsPage);

  const onSettingsClick = useCallback(() => {
    requestToWritePM().then(() => {
      setAppLocation("/settings");
    }).catch(() => {
      showAlert('You need to grant access to open settings');
    });
  }, []);

  // Set up Telegram SettingsButton (only in Telegram mode)
  useEffect(() => {
    if (!isTelegram) return;

    window.Telegram.WebApp.SettingsButton
        .onClick(onSettingsClick)
        .show();

    return () => {
      window.Telegram.WebApp.SettingsButton
          .offClick(onSettingsClick)
          .hide();
    }
  }, [onSettingsClick]);

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="App" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <div className="splash-logo-spinner" />
      </div>
    );
  }

  // In standalone mode, show login page if user is not authenticated (guest)
  if (isStandalone && (!isAuthenticated || isGuest)) {
    return <LoginPage />;
  }

  return (
    <div className="App" dir={isRTL() ? 'rtl' : 'ltr'}>
      <StandaloneHeader
        setAppLocation={setAppLocation}
        currentPage={currentPage}
      />
      <AnimatePresence mode='wait'>
        {navigationStack.map((page, index) => {
          // Only render the current (top) page
          if (index === navigationStack.length - 1) {
            return (
              <PageRenderer
                key={`${page.type}-${page.timestamp}`}
                page={page}
                index={index}
                isInitialLoad={isInitialLoad}
                setAppLocation={setAppLocation}
                chatId={chatId}
                animationDirectionsRef={animationDirectionsRef}
              />
            );
          }
          return null;
        })}
      </AnimatePresence>
      <Toast />
      <StandaloneButtons />
    </div>
  );
}

function App() {

  // Extract referer for AuthProvider
  let initRef = null;
  if (isTelegram) {
    let initAppLocation = window.Telegram?.WebApp?.initDataUnsafe?.start_param || '';
    initRef = (initAppLocation.match(/ref_([0-9_a-zA-Z]+)/) || [])[1];
    if (!initRef) {
      const instance = window.Telegram?.WebApp?.initDataUnsafe?.chat_instance;
      if (instance) {
        initRef = 'inst' + instance;
      }
    }
  }

  // Expand Telegram WebApp if not expanded
  useEffect(() => {
    if (isTelegram && window.Telegram?.WebApp && !window.Telegram.WebApp.isExpanded) {
      window.Telegram.WebApp.expand();
    }
  }, []);

  return (
    <ThemeProvider>
      <AuthProvider referer={initRef}>
        <ButtonProvider>
          <TranslationProvider>
            <ToastProvider>
              <AppInner />
            </ToastProvider>
          </TranslationProvider>
        </ButtonProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
